import React from 'react';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { ListSection, Section } from 'common';
import strings from 'strings';
import InfoLabel from 'UI/components/molecules/InfoLabel';
import { capitalizeFirstLetter } from 'UI/utils';

import { PARSED_STATUSES } from '../../constants';
import { buildTravelString } from '../../utils';
import { Benefits } from '../Benefits';
import { CompanySummary } from '../CompanySummary';
import { ReportingStructure } from '../ReportingStructure';

const COPIES = strings.inventory.jobOrders.profile.jobDescriptionTab;

const renderWorkDays = (days = []) =>
  Array.isArray(days) && days?.length > 0 ? (
    <Box display="flex" flexWrap="wrap" gridGap={8}>
      {days.map(day => (
        <Chip key={day} label={day} variant="outlined" />
      ))}
    </Box>
  ) : null;

export const JobDescriptionContent = ({ activeJobDescriptionFile, jobDescription }) => {
  const {
    benefits,
    company,
    compensation_range: compensationRange,
    employment_type: employmentType,
    key_responsibilities: responsabilities,
    language_requirements: languageRequirements,
    location,
    physical_requirements: physicalRequirements,
    preferred_qualifications: preferredQualifications,
    reports: reportingStructure,
    required_qualifications: requiredQualifications,
    role_summary: roleSummary,
    shift_hours: shiftHours,
    skills,
    travel,
    work_type: workType
  } = jobDescription || {};

  const formattedLocation =
    location && `${location?.city}, ${location?.state}, ${location?.country}`;
  const formattedEmploymentType = [
    employmentType && capitalizeFirstLetter(employmentType.replace('-', ' ')),
    workType && capitalizeFirstLetter(workType.replace('-', ' '))
  ]
    .filter(Boolean)
    .join(' / ');

  const { start_time: shiftStartTime, end_time: shiftEndTime } = shiftHours || {};
  const schedule = shiftStartTime && shiftEndTime ? `${shiftStartTime} - ${shiftEndTime}` : null;
  const workDaysToRender = renderWorkDays(shiftHours?.days);
  const travelInfoToRender = buildTravelString(travel);

  if (activeJobDescriptionFile?.status === PARSED_STATUSES.error) {
    return (
      <Typography variant="body1" color="textSecondary">
        {activeJobDescriptionFile?.result?.error || COPIES.parseError}
      </Typography>
    );
  }

  if (!activeJobDescriptionFile?.isSchemaCompliant) {
    return <Typography variant="body1">{COPIES.incompatibleStructure}</Typography>;
  }

  return (
    <>
      <Section>
        <Grid container spacing={4}>
          <Grid container item>
            {roleSummary && (
              <Grid item xs={12}>
                <InfoLabel
                  cropped={false}
                  description={roleSummary}
                  title={COPIES.sections.summary.fields.aboutRole}
                  variant="body1"
                />
              </Grid>
            )}

            {formattedLocation && (
              <Grid item xs={12} sm={6} md={4}>
                <InfoLabel
                  cropped={false}
                  description={formattedLocation}
                  title={COPIES.sections.summary.fields.location}
                  variant="body1"
                />
              </Grid>
            )}

            {formattedEmploymentType && (
              <Grid item xs={12} sm={6} md={4}>
                <InfoLabel
                  cropped={false}
                  description={formattedEmploymentType}
                  title={COPIES.sections.summary.fields.employmentType}
                  variant="body1"
                />
              </Grid>
            )}

            {schedule && (
              <Grid item xs={12} sm={6} md={4}>
                <InfoLabel
                  cropped={false}
                  description={schedule}
                  title={COPIES.sections.summary.fields.schedule}
                  variant="body1"
                />
              </Grid>
            )}

            {workDaysToRender && (
              <Grid item xs={12} sm={6} md={4}>
                <InfoLabel
                  cropped={false}
                  description={workDaysToRender}
                  title={COPIES.sections.summary.fields.workDays}
                  variant="body1"
                />
              </Grid>
            )}

            {travelInfoToRender && (
              <Grid item xs={12} sm={6} md={4}>
                <InfoLabel
                  cropped={false}
                  description={travelInfoToRender}
                  title={COPIES.sections.summary.fields.travel}
                  variant="body1"
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Section>

      <CompanySummary company={company} />
      <ListSection title={COPIES.sections.responsabilities} items={responsabilities} />
      <ListSection title={COPIES.sections.requiredQualifications} items={requiredQualifications} />
      <ListSection
        title={COPIES.sections.preferredQualifications}
        items={preferredQualifications}
      />
      <ListSection title={COPIES.sections.physicalRequirements} items={physicalRequirements} />
      <ListSection title={COPIES.sections.languageRequirements} items={languageRequirements} />
      <ListSection title={COPIES.sections.skills} items={skills} />
      <Benefits benefits={benefits} compensationRange={compensationRange} />
      <ReportingStructure reportingStructure={reportingStructure} />
    </>
  );
};
